import { isTitleElement, nodeCount, type TElement } from './elements';

/** [start, end) over the element list. */
export type TPart = { startElement: number; endElement: number };

// No character cap beside it: the editor pays per node, and a paragraph is one however long its text is.
const PART_NODE_BUDGET = 200;

// Boundaries are invisible to the reader, so aligning them to headings would buy nothing.
export const computeParts = (elements: readonly TElement[]): TPart[] => {
  if (!elements.length) return [];

  const starts: number[] = [0];
  let budget = nodeCount(elements[0]);
  for (let i = 1; i < elements.length; i++) {
    const size = nodeCount(elements[i]);
    const sectionChanged = elements[i].section !== elements[i - 1].section;
    // `budget > 0` keeps an element bigger than the whole budget from starting an empty part before itself.
    if (sectionChanged || (budget > 0 && budget + size > PART_NODE_BUDGET)) {
      starts.push(i);
      budget = 0;
    }
    budget += size;
  }

  // The title has no content of its own, so it joins the first real part rather than forming one -
  // unless that part opens a section, which the title is not inside.
  if (starts.length > 1 && starts[1] === 1 && isTitleElement(elements[0]) && !elements[1].section) {
    starts.splice(1, 1);
  }

  return starts.map((start, i) => ({
    startElement: start,
    endElement: i + 1 < starts.length ? starts[i + 1] : elements.length,
  }));
};

export const partIndexOfElement = (parts: readonly TPart[], elementIndex: number): number => {
  const found = parts.findIndex((part) => elementIndex >= part.startElement && elementIndex < part.endElement);
  return found >= 0 ? found : Math.max(parts.length - 1, 0);
};
